import { useEffect, useRef } from 'react';

/**
 * Faz o botão físico de voltar do celular (ou o gesto de "swipe
 * back") fechar só o nível interno mais recente — lightbox de foto,
 * um passo de formulário, um modal — em vez de sair da tela inteira
 * (achado real, 27/07/2026: sem isso, apertar voltar num lightbox
 * ou num passo de formulário fechava tudo de uma vez, porque o
 * navegador não sabia que existia algo "por cima" pra desfazer).
 *
 * Pilha única, compartilhada por qualquer tela que use os hooks
 * abaixo — importante quando dois níveis diferentes estão abertos ao
 * mesmo tempo (ex: lightbox de foto aberto dentro do passo 3 de um
 * formulário). Só existe 1 listener real de "popstate"; ele sempre
 * entrega o evento pro nível do TOPO da pilha (o mais recente),
 * nunca deixa dois níveis reagirem ao mesmo "voltar" (bug real
 * corrigido nesta mesma data: dois listeners independentes faziam um
 * único aperto de voltar fechar 2 níveis de uma vez em vez de 1).
 */
/* A rota atual, lida do próprio navegador. Não usa `useLocation` de
   propósito: este hook é usado por telas que nem sempre estão dentro de
   um Router (modais soltos, lightbox), e um `useLocation` fora de
   contexto lança. `pathname` puro funciona nos dois casos. */
function pathnameAtual() {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

const pilha = [];
let listenerGlobalAtivo = false;
/* ⚠⚠ CONTADOR, NÃO BOOLEANO (23/09/2026, ele: *"quando cancelo a edição do
   fluxo no livro do produto, ele sai de tudo e volta para a sala anterior"*).

   Era `let ignorarProximoPopstate = false`. Quando DOIS níveis fecham no
   mesmo clique — no livro, `editandoEtapa` e `novaEtapaEm` desligam juntos —
   cada desmonte agenda um `history.back()`. Mas `history.back()` é
   ASSÍNCRONO: os dois `popstate` chegam DEPOIS, e a trava é uma só. O
   primeiro a consome; o segundo passa direto, é tratado como "voltar" de
   verdade e fecha o livro inteiro.

   Com contador, cada `back()` programático reserva o seu. */
let popstatesAIgnorar = 0;

function garantirListenerGlobal() {
  if (listenerGlobalAtivo) return;
  listenerGlobalAtivo = true;
  window.addEventListener('popstate', () => {
    if (popstatesAIgnorar > 0) { popstatesAIgnorar -= 1; return; }
    const topo = pilha[pilha.length - 1];
    if (topo) topo();
  });
}

function empilhar(handler) {
  garantirListenerGlobal();
  window.history.pushState({ nivelSgq: true }, '');
  pilha.push(handler);
}

// Usado quando o nível fecha por outro caminho que não o botão
// físico de voltar (clique no "×", clique fora, etc.) — remove da
// pilha E consome a entrada correspondente no histórico do navegador,
// sem deixar rastro fantasma pro próximo "voltar" cair em cima. A
// flag evita que esse "voltar" programático acione o handler do
// nível que sobrou por baixo (ele não deveria reagir a isso).
function desempilharEConsumir(handler) {
  const i = pilha.lastIndexOf(handler);
  if (i === -1) return;
  pilha.splice(i, 1);
  popstatesAIgnorar += 1;
  window.history.back();
}

// Usado quando o próprio popstate disparou o handler — a entrada já
// foi consumida pelo navegador, só precisa sair da pilha.
function desempilharSemConsumir(handler) {
  const i = pilha.lastIndexOf(handler);
  if (i !== -1) pilha.splice(i, 1);
}


/**
 * Uso: dentro do componente que controla o nível (ex: um lightbox),
 *   useVoltarNavegador(!!lightbox, () => setLightbox(null));
 */
export function useVoltarNavegador(ativo, onVoltar) {
  const onVoltarRef = useRef(onVoltar);
  onVoltarRef.current = onVoltar;
  const viaPopstateRef = useRef(false);
  const rotaAoEmpilhar = useRef('');

  useEffect(() => {
    if (!ativo) return;
    const handler = () => {
      viaPopstateRef.current = true;
      desempilharSemConsumir(handler);
      onVoltarRef.current();
    };
    viaPopstateRef.current = false;
    /* Guardado ANTES do pushState: é a rota da tela que abriu o nível. */
    rotaAoEmpilhar.current = pathnameAtual();
    empilhar(handler);

    return () => {
      if (viaPopstateRef.current) return;

      /* ⚠ SAIR DA TELA NÃO É FECHAR O NÍVEL (28/08/2026, relato do Juan:
         "clico na sidebar para ir pra outra sala e ele só volta pra
         primeira aba; na segunda tentativa deixa").

         `desempilharEConsumir` faz `history.back()` para não deixar uma
         entrada fantasma. Isso é certo quando o nível fecha POR DENTRO —
         clique no ×, clique fora, Esc: a pessoa continua na mesma tela e
         a entrada precisa sumir.

         Mas a desmontagem também acontece quando a pessoa NAVEGA PARA
         OUTRA TELA. Aí o `back()` desfaz a navegação que ela acabou de
         pedir — o React Router já empurrou a rota nova, e o `back()`
         volta para a anterior. O efeito é exatamente o relatado: o
         primeiro clique parece não funcionar, o segundo funciona.

         Como distinguir: se a URL MUDOU entre empilhar e desmontar,
         houve navegação e o histórico já se resolveu sozinho — só sai da
         pilha. Se a URL é a mesma, o nível fechou por dentro e o
         `back()` continua necessário. */
      /* ⚠ `location.href` NÃO SERVE: o `pushState` do `empilhar` é feito
         SEM url, então a URL não muda ao abrir o nível — e comparar href
         nunca detectaria nada. O sinal é o `pathname` da ROTA, que o
         React Router altera ao trocar de sala e não altera ao fechar um
         modal na mesma tela. */
      if (rotaAoEmpilhar.current !== pathnameAtual()) desempilharSemConsumir(handler);
      else desempilharEConsumir(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo]);
}

/**
 * Variante pra formulário com vários passos numerados (1, 2, 3...) —
 * cada avanço de passo empilha um nível novo, e o botão voltar do
 * celular recua um passo por vez, na ordem certa, em vez de sair do
 * formulário inteiro assim que o primeiro "voltar" chega. Só ao
 * apertar voltar já no primeiro passo é que `onSairDoPrimeiro` é
 * chamado (fecha o formulário de vez, com a confirmação de praxe).
 */
export function useVoltarPassos(passo, setPasso, onSairDoPrimeiro) {
  const passoAnteriorRef = useRef(passo);
  const handlersRef = useRef([]); // 1 handler empilhado por passo acima de 1, na ordem
  const onSairRef = useRef(onSairDoPrimeiro);
  onSairRef.current = onSairDoPrimeiro;

  useEffect(() => {
    const anterior = passoAnteriorRef.current;

    if (passo > anterior) {
      for (let p = anterior + 1; p <= passo; p++) {
        const handler = () => {
          const i = handlersRef.current.indexOf(handler);
          if (i !== -1) handlersRef.current.splice(i, 1);
          setPasso(pAtual => {
            if (pAtual <= 1) { onSairRef.current(); return pAtual; }
            return pAtual - 1;
          });
        };
        empilhar(handler);
        handlersRef.current.push(handler);
      }
    } else if (passo < anterior) {
      // Passo recuou por outro caminho (clique direto no "← Voltar" da
      // tela, não o botão físico) — consome as entradas de histórico
      // que tinham sido empilhadas pra esses passos, na ordem inversa.
      const quantidade = anterior - passo;
      for (let i = 0; i < quantidade; i++) {
        const handler = handlersRef.current.pop();
        if (handler) desempilharEConsumir(handler);
      }
    }

    passoAnteriorRef.current = passo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passo]);
}

/**
 * ABA COMO NÍVEL — o botão voltar retorna à aba padrão antes de sair
 * da sala (24/08/2026, pedido do Juan).
 *
 * ── O QUE ACONTECIA ──────────────────────────────────────────
 * Os 117 modais do sistema já tratam o voltar, via `useSaidaSuave`.
 * Mas ABA não é modal: a Sala 2 abre a inspeção trocando `aba` para
 * "nova", e a troca usa `navigate(..., { replace: true })`, que por
 * definição NÃO empilha histórico.
 *
 * Resultado: com o formulário de inspeção aberto, o voltar do celular
 * pulava a sala inteira e caía na tela anterior — exatamente o que o
 * Juan descreveu, e a razão de o mecanismo dos modais não ajudar ali.
 *
 * ── A REGRA ──────────────────────────────────────────────────
 * Estando fora da aba padrão, voltar retorna à padrão. Estando NA
 * padrão, voltar sai da sala, como sempre.
 *
 * Um nível só, não um por aba visitada: quem navegou por quatro abas
 * não espera apertar voltar quatro vezes para sair — espera voltar ao
 * começo e sair. O histórico de abas dentro de uma sala não é um
 * caminho que alguém queira refazer de trás para frente.
 *
 * ── USO ──────────────────────────────────────────────────────
 *   const [aba, setAba] = useState('geral');
 *   useVoltarAba(aba, setAba, 'geral');
 */
export function useVoltarAba(aba, setAba, abaPadrao) {
  useVoltarNavegador(aba !== abaPadrao, () => setAba(abaPadrao));
}

/* ══ O VOLTAR DOS MODAIS EM TODO O SISTEMA (26/09/2026, ele: "está acontecendo em muitas outras salas
   pelo telefone, o voltar deveria voltar só pra etapa anterior") ═══════════════════════════════════
   Pôr o gancho tela por tela deixava sempre alguma de fora. Aqui, UMA vez para o sistema inteiro:
   quem abre por cima (`.modal-overlay`, `.conf-tela`) entra no histórico ao aparecer, e o voltar do
   celular o fecha — pelo × dele, pelo clique fora, ou pelo Cancelar/Voltar/Fechar/Manter, nessa ordem.
   ⚠ SEM DOBRAR COM O GANCHO: quem já usa `useVoltarNavegador` empilha no próprio efeito, logo depois
   de aparecer; por isso a decisão espera um instante e, se a pilha cresceu, deixa com o gancho.
   ⚠ FECHOU POR DENTRO (×, salvar) → a entrada é consumida; MUDOU DE ROTA → só sai da pilha. */
const SEL_NIVEL = '.modal-overlay, .conf-tela';
const geridos = new Map();   // elemento → { handler, rota }
function fecharNivel(el) {
  const vis = (b) => b && b.offsetParent !== null && !b.disabled;
  const x = [...el.querySelectorAll('.acao-fechar')].filter(vis).pop();
  if (x) { x.click(); return; }
  if (el.classList.contains('modal-overlay')) {
    el.dispatchEvent(new window.MouseEvent('click', { bubbles: true }));
    setTimeout(() => { if (!el.isConnected) return;
      const b = [...el.querySelectorAll('button')].filter(vis).find(k => /^(cancelar|voltar|fechar|manter|não)$/i.test(k.textContent.trim().replace(/^[←‹]\s*/, '')));
      if (b) b.click(); }, 60);
  }
}
function soltar(el) {
  const g = geridos.get(el); if (!g) return; geridos.delete(el);
  if (!pilha.includes(g.handler)) return;
  if (g.rota !== pathnameAtual()) desempilharSemConsumir(g.handler); else desempilharEConsumir(g.handler);
}
function adotar(el) {
  if (geridos.has(el)) return;
  const antes = pilha.length;
  geridos.set(el, { handler: null, rota: pathnameAtual() });
  setTimeout(() => {
    const g = geridos.get(el); if (!g || !el.isConnected) { geridos.delete(el); return; }
    if (pilha.length > antes) { geridos.delete(el); return; }      // um gancho já cuida deste nível
    const handler = () => { desempilharSemConsumir(handler); geridos.delete(el); fecharNivel(el); };
    g.handler = handler; empilhar(handler);
  }, 90);
}
let observando = false;
export function instalarVoltarDosModais() {
  if (observando || typeof window === 'undefined' || !window.MutationObserver) return;
  observando = true; garantirListenerGlobal();
  new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach(n => { if (n.nodeType !== 1) return; if (n.matches?.(SEL_NIVEL)) adotar(n); n.querySelectorAll?.(SEL_NIVEL).forEach(adotar); });
      m.removedNodes.forEach(n => { if (n.nodeType !== 1) return; [...geridos.keys()].forEach(el => { if (!el.isConnected && (el === n || n.contains?.(el))) { if (geridos.get(el)?.handler) soltar(el); else geridos.delete(el); } }); });
    }
  }).observe(document.body, { childList: true, subtree: true });
}

/* (V1907) A MESMA PILHA (no fim do arquivo: a bateria testar_voltar.js recorta o trecho da pilha, e um export ali dentro quebrava) para o observador das camadas (utils/camadas.js): ele empilha um nível para cada camada que aparece na tela, nesta pilha
   — e não numa segunda, que brigava com esta (achado na suíte, a Sala 11: os dois marcavam e voltavam, e um voltar sobrando fechava a ficha). */
export const pilhaVoltar = {
  empilhar: (h) => empilhar(h),
  sair: (h) => desempilharEConsumir(h),     // fechou pela tela: sai da pilha e o histórico volta junto (contado)
  saiu: (h) => desempilharSemConsumir(h),   // o voltar já consumiu (ou a pessoa navegou): só sai da pilha
};
